import { Amplify } from 'aws-amplify';
import { signOut, signInWithRedirect, fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import config from '../config';

let amplifyConfigured = false;

export function configureAmplify() {
    if (amplifyConfigured) return;

    const userPoolId = config.COGNITO_USER_POOL_ID;
    const userPoolClientId = config.COGNITO_APP_CLIENT_ID;
    const domain = config.COGNITO_DOMAIN;

    if (!userPoolId || !userPoolClientId) {
        console.warn('Cognito not configured - using legacy auth');
        return;
    }

    const amplifyConfig = {
        Auth: {
            Cognito: {
                userPoolId,
                userPoolClientId,
            },
        },
    };

    // If Hosted UI domain is configured, add OAuth settings
    if (domain) {
        const redirectUrl = window.location.origin;
        amplifyConfig.Auth.Cognito.loginWith = {
            oauth: {
                domain,
                scopes: ['email', 'openid', 'profile', 'aws.cognito.signin.user.admin'],
                redirectSignIn: [redirectUrl],
                redirectSignOut: [redirectUrl],
                responseType: 'code',
            },
        };
    }

    Amplify.configure(amplifyConfig);
    amplifyConfigured = true;
}

export function isCognitoConfigured() {
    return amplifyConfigured;
}

export function isHostedUIConfigured() {
    return amplifyConfigured && !!config.COGNITO_DOMAIN;
}

export function cognitoHostedUILogin() {
    signInWithRedirect();
}

/**
 * Listen for OAuth redirect completion.
 * Returns a promise that resolves when sign-in succeeds or rejects on failure.
 * If there's no OAuth redirect in progress, checks for an existing session.
 */
export function waitForOAuthRedirect() {
    return new Promise((resolve, reject) => {
        // Check if this is an OAuth callback (URL has 'code' param)
        const urlParams = new URLSearchParams(window.location.search);
        const hasCode = urlParams.has('code');

        if (!hasCode) {
            // No OAuth callback - check for existing session
            getCurrentUser()
                .then(() => getAccessToken().then(resolve))
                .catch(() => resolve(null));
            return;
        }

        // OAuth callback in progress - wait for Hub event
        const unsubscribe = Hub.listen('auth', ({ payload }) => {
            if (payload.event === 'signInWithRedirect') {
                unsubscribe();
                getAccessToken().then(resolve).catch(() => resolve(null));
            }
            if (payload.event === 'signInWithRedirect_failure') {
                unsubscribe();
                reject(new Error('OAuth sign-in failed'));
            }
        });

        // Timeout after 10 seconds
        setTimeout(() => {
            unsubscribe();
            resolve(null);
        }, 10000);
    });
}

export async function cognitoSignOut() {
    // Clear local Amplify session
    try {
        await signOut({ global: true });
    } catch (e) {
        // Ignore errors - we're clearing everything anyway
    }

    // For Hosted UI, redirect to Cognito's logout endpoint to clear its session cookie
    if (isHostedUIConfigured()) {
        const domain = config.COGNITO_DOMAIN;
        const clientId = config.COGNITO_APP_CLIENT_ID;
        const logoutUri = encodeURIComponent(window.location.origin);
        window.location.href = `https://${domain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
    }
}

export async function getAccessToken() {
    try {
        const session = await fetchAuthSession();
        return session.tokens?.idToken?.toString() || null;
    } catch {
        return null;
    }
}

export async function getCognitoUser() {
    try {
        const user = await getCurrentUser();
        return user;
    } catch {
        return null;
    }
}

export async function isAuthenticated() {
    try {
        await getCurrentUser();
        return true;
    } catch {
        return false;
    }
}
